'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const STATS = [
{ number: '2.4M+', label: 'Aktive Mitglieder', sublabel: 'in Deutschland, Österreich & Schweiz' },
{ number: '48K+', label: 'Bewertungen', sublabel: 'Ø 4.8 Sterne auf Trustpilot' },
{ number: '12K', label: 'Neue Matches', sublabel: 'werden täglich geknüpft' },
{ number: '94%', label: 'Weiterempfehlung', sublabel: 'unserer aktiven Nutzer' }];


const TESTIMONIALS = [
{
  text: '„Nach nur 3 Wochen habe ich jemanden kennengelernt, der wirklich zu mir passt. Die Matches in meiner Stadt waren überraschend gut.“',
  name: 'Lena K.',
  city: 'Frankfurt',
  age: 31,
  src: "https://images.unsplash.com/photo-1531866538087-7a785387e383",
  alt: 'Frau mit langen Haaren lächelt warmherzig',
  stars: 5,
  rotate: '-rotate-1'
},
{
  text: '„Endlich eine Plattform, die nicht wie eine Datingapp aus 2010 aussieht. Einfach zu bedienen und die Leute sind wirklich aktiv.“',
  name: 'Thomas M.',
  city: 'Wien',
  age: 38,
  src: "https://images.unsplash.com/photo-1653508036272-a3c11c6d5d8a",
  alt: 'Mann mit Bart lächelt in die Kamera',
  stars: 5,
  rotate: 'rotate-1'
},
{
  text: '„Die Postleitzahl-Suche ist Gold wert. Ich treffe echte Menschen aus meiner Nachbarschaft — kein Catfishing, keine Enttäuschungen.“',
  name: 'Michaela S.',
  city: 'Zürich',
  age: 29,
  src: "https://images.unsplash.com/photo-1673799809016-26226917091b",
  alt: 'Junge Frau mit Lächeln im Freien',
  stars: 5,
  rotate: '-rotate-0.5'
}];


export default function SocialProofSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15 }
    );

    const reveals = sectionRef?.current?.querySelectorAll('.reveal, .stagger-children');
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 md:py-36 px-4 md:px-6 relative overflow-hidden"
    style={{ background: 'linear-gradient(180deg, #0C0A0E 0%, #110C15 50%, #0C0A0E 100%)' }}>
      {/* Subtle divider glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-40"
      style={{ background: 'linear-gradient(to bottom, transparent, rgba(232,49,90,0.5), transparent)' }} />
      <div className="max-w-7xl mx-auto">

        {/* Section label */}
        <div className="reveal text-center mb-20">
          <span className="text-xs font-semibold uppercase tracking-widest px-5 py-2.5 rounded-full"
          style={{ color: '#FF8CA8', background: 'rgba(232,49,90,0.12)', border: '1px solid rgba(232,49,90,0.25)', boxShadow: '0 4px 16px rgba(232,49,90,0.15)' }}>
            Vertrauen durch Zahlen
          </span>
          <h2 className="section-headline text-white mt-8 mb-5">
            Echte Verbindungen,<br />
            <span className="text-gradient italic">messbare Ergebnisse.</span>
          </h2>
          <p className="max-w-lg mx-auto text-lg font-light leading-relaxed"
          style={{ color: 'rgba(245,240,247,0.55)' }}>
            Über 2 Millionen Singles vertrauen xkuss.com — weil wir auf echte Matches setzen, nicht auf Quantität.
          </p>
        </div>

        {/* Stats bento — asymmetric */}
        <div className="stagger-children bento-grid grid-cols-2 md:grid-cols-4 mb-20 gap-5"
        style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {STATS?.map((stat, i) =>
          <div key={i} className="feature-card rounded-3xl p-7 md:p-9 text-center"
          style={{ background: 'var(--gradient-card)' }}>
              <p className="font-display text-4xl md:text-5xl font-bold mb-2 text-gradient glow-text">
                {stat?.number}
              </p>
              <p className="text-sm font-semibold text-white mb-1.5">{stat?.label}</p>
              <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,240,247,0.45)' }}>{stat?.sublabel}</p>
            </div>
          )}
        </div>

        {/* Testimonials — asymmetric bento */}
        <div className="reveal grid md:grid-cols-3 gap-6 md:gap-7">
          {TESTIMONIALS?.map((t, i) =>
          <div key={i} className={`testimonial-card glass-card rounded-3xl p-7 md:p-9 flex flex-col justify-between ${t?.rotate}`}>
              <div>
                <div className="flex gap-1 mb-5">
                  {[...Array(t?.stars)]?.map((_, j) =>
                <span key={j} className="text-base" style={{ color: '#FF6B35', filter: 'drop-shadow(0 2px 4px rgba(255,107,53,0.3))' }}>★</span>
                )}
                </div>
                <p className="text-base leading-relaxed mb-7 font-light" style={{ color: 'rgba(245,240,247,0.8)' }}>
                  {t?.text}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0"
              style={{ border: '2.5px solid rgba(232,49,90,0.35)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  <AppImage src={t?.src} alt={t?.alt} width={48} height={48} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{t?.name}, {t?.age}</p>
                  <p className="text-xs" style={{ color: 'rgba(245,240,247,0.45)' }}>{t?.city}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}